/**
 * @flow
 * */
import { put, takeLatest, takeEvery } from 'redux-saga/effects';

import riskWeeklyReportSDK from 'modules/RiskWeeklyReport/core/sdk/RiskWeeklyReportSDK';
import RISK_WEEKLY_ACTION_TYPES from 'modules/RiskWeeklyReport/store/actions/types';
import {
  // DISABLED - action creators for the five views the first release does not ship.
  // RMT8993, 2026-09-14; see docs/report_docs/13-live-dashboard-only.md.
  // setFetchGrmtSuccess,
  // setFetchGrmtFailure,
  // setFetchGrmtSettingDetailSuccess,
  // setFetchGrmtSettingDetailFailure,
  // setFetchEmployeeAnalyticsSuccess,
  // setFetchEmployeeAnalyticsFailure,
  // setFetchEmployeeDetailSuccess,
  // setFetchEmployeeDetailFailure,
  // setFetchPartnerTriggerSuccess,
  // setFetchPartnerTriggerFailure,
  setFetchCalendarSuccess,
  setFetchCalendarFailure,
  setFetchExploreCubeSuccess,
  setFetchDailyStatisticsSuccess,
  setFetchDailyStatisticsFailure,
  setFetchExploreCubeFailure,
  // setFetchInsightsSuccess,
  // setFetchInsightsFailure,
  setFetchDepartmentDetailSuccess,
  setFetchDepartmentDetailFailure,
  setFetchPartnerCoverageSuccess,
  setFetchPartnerCoverageFailure,
  setFetchOverviewSuccess,
  setFetchOverviewFailure,
  setFetchWeeksSuccess,
  setFetchWeeksFailure,
  setClearServerCacheSuccess,
  setClearServerCacheFailure
} from 'modules/RiskWeeklyReport/store/actions';

class RiskWeeklyReportController {
  /**
   * Drops the backend's caches, then reloads the page if asked to.
   *
   * A full reload rather than refetching this view: the point of clearing the cache is that
   * nothing stale is being shown, and the store holds cached payloads for every other tab too -
   * drilldowns, insights, the GRMT tables. Refetching only the current view would leave the rest
   * exactly as stale as before, which is the failure the operator was trying to fix.
   *
   * An AbortError is NOT swallowed here, unlike in the reads. This call is not abortable, so an
   * abort would mean something genuinely went wrong and the operator needs to know the cache may
   * or may not have been cleared.
   *
   * @param {Object} action - { payload: { reloadOnSuccess } }
   * @returns {void} -
   */
  static* clearServerCache({ payload }) {
    try {
      const data = yield riskWeeklyReportSDK.clearCache();
      yield put(setClearServerCacheSuccess({ data }));

      if (!payload || payload.reloadOnSuccess !== false) {
        // After the success action, so the result is in the store if the reload is slow enough
        // for the banner to paint.
        window.location.reload();
      }
    } catch (err) {
      yield put(setClearServerCacheFailure({ error: err.message }));
    }
  }

  static* fetchWeeks({ payload }) {
    try {
      const data = yield riskWeeklyReportSDK.getWeeks(payload);
      yield put(setFetchWeeksSuccess({ data }));
    } catch (err) {
      // An aborted call is a superseded request, not a failure: reporting it would flash an
      // error every time the operator changes the week range.
      if (err.name === 'AbortError') return;
      yield put(setFetchWeeksFailure({ error: err.message }));
    }
  }

  static* fetchOverview({ payload }) {
    try {
      const data = yield riskWeeklyReportSDK.getOverview(payload);
      yield put(setFetchOverviewSuccess({ data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchOverviewFailure({ error: err.message }));
    }
  }

  static* fetchDepartmentDetail({ payload }) {
    const { key, ...filters } = payload;
    try {
      const data = yield riskWeeklyReportSDK.getDepartmentDetail(filters);
      yield put(setFetchDepartmentDetailSuccess({ key, data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchDepartmentDetailFailure({ key, error: err.message }));
    }
  }

  /**
   * The partner coverage probe.
   *
   * Only ever dispatched by the panel opening, never by a filter change: the answer is about the
   * partner list rather than about the selected range, and this is the one call that makes the
   * server read outside the report's partner scope.
   *
   * @param {Object} action - { payload: { week } }
   * @returns {void} -
   */
  static* fetchPartnerCoverage({ payload }) {
    try {
      const data = yield riskWeeklyReportSDK.getPartnerCoverage(payload);
      yield put(setFetchPartnerCoverageSuccess({ data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchPartnerCoverageFailure({ error: err.message }));
    }
  }

  // =================================================================================
  // DISABLED - sagas for the five views the first production release does not ship.
  // RMT8993, 2026-09-14; see docs/report_docs/13-live-dashboard-only.md.
  // =================================================================================
  // static* fetchGrmt({ payload }) {
    // try {
      // const data = yield riskWeeklyReportSDK.getGrmtUnderTest(payload);
      // yield put(setFetchGrmtSuccess({ data }));
    // } catch (err) {
      // if (err.name === 'AbortError') return;
      // yield put(setFetchGrmtFailure({ error: err.message }));
    // }
  // }

  // static* fetchGrmtSettingDetail({ payload }) {
    // const { key, ...filters } = payload;
    // try {
      // const data = yield riskWeeklyReportSDK.getGrmtSettingDetail(filters);
      // yield put(setFetchGrmtSettingDetailSuccess({ key, data }));
    // } catch (err) {
      // if (err.name === 'AbortError') return;
      // yield put(setFetchGrmtSettingDetailFailure({ key, error: err.message }));
    // }
  // }

  // static* fetchEmployeeAnalytics({ payload }) {
    // try {
      // const data = yield riskWeeklyReportSDK.getEmployeeAnalytics(payload);
      // yield put(setFetchEmployeeAnalyticsSuccess({ data }));
    // } catch (err) {
      // if (err.name === 'AbortError') return;
      // yield put(setFetchEmployeeAnalyticsFailure({ error: err.message }));
    // }
  // }

  // static* fetchEmployeeDetail({ payload }) {
    // const { key, ...filters } = payload;
    // try {
      // const data = yield riskWeeklyReportSDK.getEmployeeDetail(filters);
      // yield put(setFetchEmployeeDetailSuccess({ key, data }));
    // } catch (err) {
      // if (err.name === 'AbortError') return;
      // yield put(setFetchEmployeeDetailFailure({ key, error: err.message }));
    // }
  // }

  // static* fetchPartnerTrigger({ payload }) {
    // const { key, ...filters } = payload;
    // try {
      // const data = yield riskWeeklyReportSDK.getPartnerTrigger(filters);
      // yield put(setFetchPartnerTriggerSuccess({ key, data }));
    // } catch (err) {
      // if (err.name === 'AbortError') return;
      // yield put(setFetchPartnerTriggerFailure({ key, error: err.message }));
    // }
  // }

  static* fetchCalendar() {
    try {
      const data = yield riskWeeklyReportSDK.getCalendar();
      yield put(setFetchCalendarSuccess({ data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchCalendarFailure({ error: err.message }));
    }
  }

  static* fetchDailyStatistics({ payload }) {
    const { key, ...filters } = payload;
    try {
      const data = yield riskWeeklyReportSDK.getDailyStatistics(filters);
      yield put(setFetchDailyStatisticsSuccess({ key, data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchDailyStatisticsFailure({ key, error: err.message }));
    }
  }

  static* fetchExploreCube({ payload }) {
    const { key, ...filters } = payload;
    try {
      const data = yield riskWeeklyReportSDK.getExploreCube(filters);
      yield put(setFetchExploreCubeSuccess({ key, data }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      yield put(setFetchExploreCubeFailure({ key, error: err.message }));
    }
  }

  // static* fetchInsights({ payload }) {
    // try {
      // const data = yield riskWeeklyReportSDK.getInsights(payload);
      // yield put(setFetchInsightsSuccess({ data }));
    // } catch (err) {
      // if (err.name === 'AbortError') return;
      // yield put(setFetchInsightsFailure({ error: err.message }));
    // }
  // }

  static* cancelSdkCall({ payload }) {
    yield riskWeeklyReportSDK.cancelCalls(payload);
  }
}

export default function* riskWeeklyReportController(): Iterator<Object> {
  // takeLatest: only the newest range matters, so a slow earlier response is discarded.
  yield takeLatest(RISK_WEEKLY_ACTION_TYPES.FETCH_WEEKS, RiskWeeklyReportController.fetchWeeks);
  yield takeLatest(RISK_WEEKLY_ACTION_TYPES.FETCH_OVERVIEW, RiskWeeklyReportController.fetchOverview);
  // takeEvery, not takeLatest: each department is a separate request and one must not cancel
  // another's response.
  yield takeEvery(
    RISK_WEEKLY_ACTION_TYPES.FETCH_DEPARTMENT_DETAIL,
    RiskWeeklyReportController.fetchDepartmentDetail
  );
  // takeLatest: the probe is a single panel, and re-opening it while the first read is still
  // running is one intent — the newer request supersedes.
  yield takeLatest(
    RISK_WEEKLY_ACTION_TYPES.FETCH_PARTNER_COVERAGE,
    RiskWeeklyReportController.fetchPartnerCoverage
  );
  // DISABLED - watchers for the five views the first release does not ship.  RMT8993.
  // yield takeLatest(RISK_WEEKLY_ACTION_TYPES.FETCH_GRMT, RiskWeeklyReportController.fetchGrmt);
  // // takeEvery for the same reason as the department drilldown: each brand's table can have its own
  // // row open, and the SDK already cancels a superseded request for the same row.
  // yield takeEvery(
    // RISK_WEEKLY_ACTION_TYPES.FETCH_GRMT_SETTING_DETAIL,
    // RiskWeeklyReportController.fetchGrmtSettingDetail
  // );
  // yield takeLatest(
    // RISK_WEEKLY_ACTION_TYPES.FETCH_EMPLOYEE_ANALYTICS,
    // RiskWeeklyReportController.fetchEmployeeAnalytics
  // );
  // // takeEvery: one open row per brand, and the SDK cancels a superseded request for the same row.
  // yield takeEvery(
    // RISK_WEEKLY_ACTION_TYPES.FETCH_EMPLOYEE_DETAIL,
    // RiskWeeklyReportController.fetchEmployeeDetail
  // );
  // // takeEvery: the two grains are independent requests, and the SDK cancels a repeat of the same
  // // grain rather than letting partner cancel trigger.
  // yield takeEvery(
    // RISK_WEEKLY_ACTION_TYPES.FETCH_PARTNER_TRIGGER,
    // RiskWeeklyReportController.fetchPartnerTrigger
  // );
  // takeLatest: coverage is one answer about the store, and a second ask supersedes the first.
  yield takeLatest(RISK_WEEKLY_ACTION_TYPES.FETCH_CALENDAR, RiskWeeklyReportController.fetchCalendar);
  // takeLatest: one day is on screen at a time, and the next one supersedes it.
  yield takeLatest(
    RISK_WEEKLY_ACTION_TYPES.FETCH_DAILY_STATISTICS,
    RiskWeeklyReportController.fetchDailyStatistics
  );
  // takeEvery: the three Explore grains are independent, and the SDK cancels a repeat of the same
  // grain rather than letting one grain cancel another.
  yield takeEvery(
    RISK_WEEKLY_ACTION_TYPES.FETCH_EXPLORE_CUBE,
    RiskWeeklyReportController.fetchExploreCube
  );
  // yield takeLatest(RISK_WEEKLY_ACTION_TYPES.FETCH_INSIGHTS, RiskWeeklyReportController.fetchInsights);
  // takeLatest: two clicks on Clear cache are one intent, and the second supersedes the first.
  yield takeLatest(
    RISK_WEEKLY_ACTION_TYPES.CLEAR_SERVER_CACHE,
    RiskWeeklyReportController.clearServerCache
  );
  yield takeEvery(RISK_WEEKLY_ACTION_TYPES.CANCEL_SDK_CALL, RiskWeeklyReportController.cancelSdkCall);
}
